// frontend/src/utils/authRedirectState.ts

/**
 * Serializable action types for auth redirect flow.
 * Since we can't serialize functions to sessionStorage,
 * we use typed actions that can be reconstructed after redirect.
 */
export type PendingActionType =
  | 'add_to_collection'
  | 'add_to_my_collections'
  | 'generate_collection'
  | 'save_solution'
  | 'save_all_solutions'
  | 'verify_solution'
  | 'create_collection'
  | 'delete_collection'
  | 'open_chat'
  | 'toggle_completion'
  | 'generate_animation'
  | 'generate_walkthrough'
  | 'generic';

export interface PendingAction {
  type: PendingActionType;
  data: Record<string, any>;
  returnUrl: string;
  timestamp: number;
}

const STORAGE_KEY = 'aiprep_pending_auth_action';
const RETURN_URL_KEY = 'aiprep_auth_return_url';

/**
 * Save a pending action before auth redirect
 */
export function savePendingAction(action: Omit<PendingAction, 'timestamp' | 'returnUrl'>): void {
  const fullAction: PendingAction = {
    ...action,
    returnUrl: window.location.pathname + window.location.search + window.location.hash,
    timestamp: Date.now(),
  };
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fullAction));
  sessionStorage.setItem(RETURN_URL_KEY, fullAction.returnUrl);
}

/**
 * Get pending action after redirect
 */
export function getPendingAction(): PendingAction | null {
  const stored = sessionStorage.getItem(STORAGE_KEY);
  if (!stored) return null;

  try {
    const action = JSON.parse(stored) as PendingAction;
    // Check if action is too old (> 30 seconds)
    // OAuth redirects should complete within seconds, not minutes
    if (Date.now() - action.timestamp > 30 * 1000) {
      console.log('[authRedirectState] Pending action expired (>30s), clearing');
      clearPendingAction();
      return null;
    }
    return action;
  } catch (e) {
    console.error('Failed to parse pending action:', e);
    clearPendingAction();
    return null;
  }
}

/**
 * Clear pending action after execution or expiry
 */
export function clearPendingAction(): void {
  sessionStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem(RETURN_URL_KEY);
}

/**
 * Get the return URL saved before redirect
 */
export function getReturnUrl(): string | null {
  return sessionStorage.getItem(RETURN_URL_KEY);
}
